<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useChatStore } from '../../stores/chatStore';
import DETLLMCall from './DETLLMCall.vue';

const { t } = useI18n();

interface DETStep {
    record_id: string;
    completed_timestamp?: string | null;
    subroutines_launched_timestamp?: string | null;
}

interface DETSubroutine {
    record_id: string;
    completed_timestamp?: string | null;
    started_timestamp?: string | null;
}

const chatStore = useChatStore();
const viewMode = ref<'structured' | 'markdown'>('structured');
const expandedSteps = ref<Record<string, boolean>>({});
const expandedSubroutines = ref<Record<string, boolean>>({});
const copyFeedback = ref(false);

// Cast at the boundary — DET trace shape is open and authored by the backend;
// the frontend reads many fields off it and a strict interface would be churn.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const trace = computed(() => chatStore.currentDETDebugTrace as Record<string, any> | null);

function toggleStep(stepId: string) {
    expandedSteps.value[stepId] = !expandedSteps.value[stepId];
}
function isStepExpanded(stepId: string) {
    return !!expandedSteps.value[stepId];
}
function toggleSubroutine(subId: string) {
    expandedSubroutines.value[subId] = !expandedSubroutines.value[subId];
}
function isSubroutineExpanded(subId: string) {
    return !!expandedSubroutines.value[subId];
}

function stepStatus(step: DETStep) {
    if (step.completed_timestamp) return 'completed';
    if (step.subroutines_launched_timestamp) return 'subroutines';
    return 'running';
}
function subStatus(sub: DETSubroutine) {
    if (sub.completed_timestamp) return 'completed';
    if (sub.started_timestamp) return 'running';
    return 'pending';
}

function formatTimestamp(ts: string | number | null | undefined) {
    if (!ts) return '-';
    return new Date(ts).toLocaleString();
}

function prettyJson(raw: string | null | undefined) {
    if (!raw) return '';
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
        return raw;
    }
}

async function copyMarkdown() {
    if (!trace.value) return;
    try {
        await navigator.clipboard.writeText(String(trace.value.markdown));
        copyFeedback.value = true;
        setTimeout(() => {
            copyFeedback.value = false;
        }, 1500);
    } catch (err) {
        console.error('Failed to copy markdown:', err);
    }
}
</script>

<template>
    <div v-if="trace" class="det-debug-view">
        <div class="det-debug-header">
            <div class="det-debug-header-left">
                <h2>{{ trace.workflow_type }}</h2>
                <span
                    class="det-debug-status-badge"
                    :class="trace.completed_timestamp ? 'completed' : 'running'"
                >
                    {{ trace.completed_timestamp ? t('debug.statusCompleted') : t('debug.statusInProgress') }}
                </span>
            </div>
            <div class="det-debug-header-right">
                <div class="det-debug-view-toggle">
                    <button
                        :class="{ active: viewMode === 'structured' }"
                        @click="viewMode = 'structured'"
                    >
                        {{ t('debug.viewStructured') }}
                    </button>
                    <button
                        :class="{ active: viewMode === 'markdown' }"
                        @click="viewMode = 'markdown'"
                    >
                        {{ t('debug.viewMarkdown') }}
                    </button>
                </div>
                <button
                    v-if="viewMode === 'markdown'"
                    class="det-debug-copy-btn"
                    @click="copyMarkdown"
                >
                    <span class="material-icons">content_copy</span>
                    {{ copyFeedback ? t('debug.copied') : t('debug.copy') }}
                </button>
            </div>
        </div>

        <div v-if="viewMode === 'structured'" class="det-debug-body">
            <div class="det-debug-workflow-meta">
                <div class="det-debug-meta-row">
                    <span class="det-debug-meta-label">{{ t('debug.startedLabel') }}</span>
                    <span>{{ formatTimestamp(trace.started_timestamp) }}</span>
                </div>
                <div v-if="trace.completed_timestamp" class="det-debug-meta-row">
                    <span class="det-debug-meta-label">{{ t('debug.completedLabel') }}</span>
                    <span>{{ formatTimestamp(trace.completed_timestamp) }}</span>
                </div>
                <div class="det-debug-meta-row">
                    <span class="det-debug-meta-label">{{ t('debug.stepsLabel') }}</span>
                    <span>{{ trace.steps.length }}</span>
                </div>
            </div>

            <details class="det-debug-section">
                <summary>{{ t('debug.workflowArguments') }}</summary>
                <pre class="det-debug-mono">{{ prettyJson(trace.arguments) }}</pre>
            </details>

            <details v-if="trace.output" class="det-debug-section">
                <summary>{{ t('debug.workflowOutput') }}</summary>
                <pre class="det-debug-mono">{{ prettyJson(trace.output) }}</pre>
            </details>

            <!-- Workflow-level LLM calls (e.g. intent routing) — before any step -->
            <div
                v-if="trace.workflow_llm_calls && trace.workflow_llm_calls.length"
                class="det-debug-llm-section"
            >
                <div class="det-debug-llm-section-title">Workflow LLM Calls</div>
                <DETLLMCall
                    v-for="lc in trace.workflow_llm_calls"
                    :key="lc.record_id"
                    :call="lc"
                />
            </div>

            <div v-for="(step, idx) in trace.steps" :key="step.record_id" class="det-debug-step">
                <div class="det-debug-step-header" @click="toggleStep(step.record_id)">
                    <span class="det-debug-expand-icon material-icons">
                        {{ isStepExpanded(step.record_id) ? 'expand_more' : 'chevron_right' }}
                    </span>
                    <span class="det-debug-step-number">{{ idx + 1 }}</span>
                    <span class="det-debug-step-kind">{{ step.step_kind }}</span>
                    <span class="det-debug-status-dot" :class="stepStatus(step)"></span>
                    <span v-if="step.next_step_kind" class="det-debug-next-step">
                        &rarr; {{ step.next_step_kind }}
                    </span>
                    <span v-if="step.llm_calls.length" class="det-debug-llm-count">
                        {{ t('debug.llmCallCount', step.llm_calls.length) }}
                    </span>
                    <span v-if="step.subroutines.length" class="det-debug-sub-count">
                        {{ t('debug.subroutineCount', step.subroutines.length) }}
                    </span>
                </div>

                <div v-if="isStepExpanded(step.record_id)" class="det-debug-step-body">
                    <div class="det-debug-args-label">{{ t('debug.arguments') }}</div>
                    <pre class="det-debug-mono">{{ prettyJson(step.arguments) }}</pre>

                    <div v-if="step.llm_calls.length" class="det-debug-llm-section">
                        <div class="det-debug-llm-section-title">{{ t('debug.stepLlmCalls') }}</div>
                        <DETLLMCall
                            v-for="lc in step.llm_calls"
                            :key="lc.record_id"
                            :call="lc"
                        />
                    </div>

                    <div v-if="step.subroutines.length" class="det-debug-subroutines-section">
                        <div class="det-debug-subroutines-title">{{ t('debug.subroutines') }}</div>
                        <div
                            v-for="sub in step.subroutines"
                            :key="sub.record_id"
                            class="det-debug-subroutine"
                        >
                            <div
                                class="det-debug-sub-header"
                                @click="toggleSubroutine(sub.record_id)"
                            >
                                <span class="det-debug-expand-icon material-icons">
                                    {{
                                        isSubroutineExpanded(sub.record_id)
                                            ? 'expand_more'
                                            : 'chevron_right'
                                    }}
                                </span>
                                <span class="det-debug-sub-kind">{{ sub.subroutine_kind }}</span>
                                <span class="det-debug-status-dot" :class="subStatus(sub)"></span>
                                <span v-if="sub.llm_calls.length" class="det-debug-llm-count">
                                    {{ t('debug.llmCallCount', sub.llm_calls.length) }}
                                </span>
                            </div>

                            <div
                                v-if="isSubroutineExpanded(sub.record_id)"
                                class="det-debug-sub-body"
                            >
                                <div class="det-debug-side-by-side">
                                    <div class="det-debug-side-col">
                                        <div class="det-debug-side-label">{{ t('debug.arguments') }}</div>
                                        <pre class="det-debug-mono">{{
                                            prettyJson(sub.arguments)
                                        }}</pre>
                                    </div>
                                    <div class="det-debug-side-col">
                                        <div class="det-debug-side-label">{{ t('debug.output') }}</div>
                                        <pre class="det-debug-mono">{{
                                            sub.output ? prettyJson(sub.output) : t('debug.none')
                                        }}</pre>
                                    </div>
                                </div>

                                <div v-if="sub.llm_calls.length" class="det-debug-llm-section">
                                    <div class="det-debug-llm-section-title">
                                        {{ t('debug.subroutineLlmCalls') }}
                                    </div>
                                    <DETLLMCall
                                        v-for="lc in sub.llm_calls"
                                        :key="lc.record_id"
                                        :call="lc"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="viewMode === 'markdown'" class="det-debug-body">
            <pre class="det-debug-markdown-pre">{{ trace.markdown }}</pre>
        </div>
    </div>
</template>
